<template>
  <div class="rounded-lg border border-neutral-300 bg-white p-4 pb-2">
    <div class="mb-4 flex items-center justify-between">
      <h6 class="text-sm font-semibold tracking-wide text-secondary-500">{{ t('connector', { id: evseId }) }}</h6>

      <font-awesome-icon
        v-if="removable"
        :icon="['far', 'trash-can']"
        class="cursor-pointer text-neutral-400 hover:text-neutral-500"
        size="1x"
        @click="emit('remove', evseId)"
      />
    </div>

    <div class="grid grid-cols-2 gap-4">
      <div class="full-width-grid-row">
        <InfuseField :label="t('connectorType.label')" size="small">
          <InfuseSelect
            :label="t('connectorType.placeholder')"
            :modelValue="connector.connectorType"
            :options="
              Object.keys(ConnectorType)
                .map((key: any) => {
                  return { key: t(`connectorType.options.${key}`), value: key };
                })
            "
            name="connectorType"
            size="small"
            @update:modelValue="onConnectorTypeUpdate"
          />
        </InfuseField>

        <p
          v-if="connector.connectorType === ConnectorType.UNSPECIFIED"
          class="mb-4 text-xs text-warning-500"
          style="max-width: 340px"
        >
          {{ t('connectorType.ocpiWarning') }}
        </p>
      </div>

      <InfuseField :label="t('voltage.label')" size="small">
        <InfuseInput
          v-model="connector.voltage"
          :min="1"
          :placeholder="t('voltage.placeholder')"
          class="w-full"
          size="small"
          type="number"
          required
          @update:modelValue="capMaxElectricPower"
        />
      </InfuseField>

      <InfuseField :label="t('maxAmperage.label')" size="small">
        <InfuseInput
          v-model="connector.maxAmperage"
          :min="1"
          :placeholder="t('maxAmperage.placeholder')"
          class="w-full"
          size="small"
          type="number"
          required
          @update:modelValue="capMaxElectricPower"
        />
      </InfuseField>

      <InfuseField :label="t('current.label')" size="small">
        <InfuseSelect
          v-model="connector.current"
          :disabled="currentLocked"
          :label="t('current.placeholder')"
          :options="
            Object.keys(Current)
              .map((key: any) => {
                return { key: t(`current.options.${key}`), value: key };
              })
          "
          name="current"
          size="small"
          @update:modelValue="onCurrentUpdate"
        />
      </InfuseField>

      <InfuseField :label="t('phase.label')" size="small">
        <InfuseSelect
          v-model="connector.phases"
          :disabled="phaseLocked"
          :label="t('phase.placeholder')"
          :options="
            Object.keys(Phase)
              .filter((val) => isNaN(Number(val)) === false)
              .map((key: any) => {
                return { key: t(`phase.options.${key}`), value: key };
              })
          "
          name="phases"
          size="small"
          @update:modelValue="capMaxElectricPower"
        />
      </InfuseField>

      <div class="full-width-grid-row">
        <InfuseField :label="t('maxElectricPower.label')" size="small">
          <InfuseSlider
            v-model="connector.maxElectricPower"
            :disabled="
              !(
                connector.maxAmperage &&
                connector.voltage &&
                (connector.phases || connector.phases === Phase.UNKNOWN_NA)
              )
            "
            :max="
              (connector.maxAmperage *
                connector.voltage *
                (connector.phases === Phase.UNKNOWN_NA ? 1 : connector.phases)) /
              1000
            "
            :min="0"
            :step="0.1"
            :requireValidStep="false"
            size="small"
          />
        </InfuseField>
      </div>

      <div v-if="!hideCapabilities" class="full-width-grid-row">
        <InfuseField :label="t('capabilities.label')" size="small">
          <CapabilitySelector
            :evse-id="props.modelValue.evseIdentifier"
            :read-only="false"
            :selected-capabilities="props.modelValue.evseCapabilities"
            @select="(capability: Capability) => onClickCapability(capability)"
          />
        </InfuseField>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { Capability, ConnectorType, Current, Phase } from '@infuse/api/cpms/config';
import { InfuseField, InfuseInput, InfuseSelect, InfuseSlider } from '@infuse/ui/components';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import CapabilitySelector from './CapabilitySelector.vue';
import { FlatConnector } from './model';

interface Emits {
  (e: 'remove', evseId: number): void;
}

const emit = defineEmits<Emits>();

interface Props {
  modelValue: FlatConnector;
  evseId: number;
  hideCapabilities: boolean;
  removable: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  evseId: 1,
  hideCapabilities: false,
  removable: true
});

const { t } = useI18n();

const connector = ref<FlatConnector>({});

const currentLocked = ref<boolean>(true);
const phaseLocked = ref<boolean>(true);

onMounted(() => {
  connector.value = props.modelValue;
});

function resetLocks() {
  currentLocked.value = false;
  phaseLocked.value = false;
}

function capMaxElectricPower() {
  // After a change in Current, Phases, Max. Amperage, (Max) Voltage
  // the maximum value for 'maxElectricPower' might change. If it does,
  // and the current value is above the new maximum, set it to that
  // new maximum.
  if (
    !connector.value.maxAmperage ||
    connector.value.maxAmperage === 0 ||
    !connector.value.voltage ||
    connector.value.voltage === 0 ||
    (!connector.value.phases && connector.value.phases !== 0) ||
    !connector.value.maxElectricPower
  ) {
    return;
  }

  const newDerivedMaxElectricPower =
    (connector.value.maxAmperage *
      connector.value.voltage *
      (connector.value.phases === 0 ? 1 : connector.value.phases)) /
    1000;

  if (connector.value.maxElectricPower > newDerivedMaxElectricPower) {
    connector.value.maxElectricPower = Math.round(Math.floor(newDerivedMaxElectricPower * 10)) / 10;
  }
}

const onClickCapability = (capability: Capability) => {
  if (!connector.value.evseCapabilities) {
    connector.value.evseCapabilities = [];
  }

  const index = connector.value.evseCapabilities.findIndex((c) => c === capability);
  if (index !== -1) {
    connector.value.evseCapabilities.splice(index, 1);
  } else {
    connector.value.evseCapabilities.push(capability);
  }
};

function onCurrentUpdate(current: Current) {
  connector.value.current = current;

  switch (current) {
    case Current.AC:
      phaseLocked.value = false;
      break;
    case Current.DC:
      phaseLocked.value = true;
      connector.value = Object.assign(connector.value, {
        phases: Phase.UNKNOWN_NA
      });
      break;
    case Current.UNSPECIFIED:
      phaseLocked.value = false;
      break;
  }

  capMaxElectricPower();
}

function onConnectorTypeUpdate(connectorType: ConnectorType) {
  connector.value.connectorType = connectorType;
  connector.value.maxElectricPower = 0;

  resetLocks();

  switch (connectorType) {
    case ConnectorType.C_TYPE_1:
      connector.value = Object.assign(connector.value, {
        current: Current.AC
      });
      currentLocked.value = true;
      break;
    case ConnectorType.C_TYPE_2:
      connector.value = Object.assign(connector.value, {
        voltage: 230,
        current: Current.AC
      });
      currentLocked.value = true;
      break;
    case ConnectorType.S_TYPE_2:
    case ConnectorType.S_TYPE_3:
      connector.value = Object.assign(connector.value, {
        current: Current.AC
      });
      currentLocked.value = true;
      break;
    case ConnectorType.C_CCS_1:
    case ConnectorType.C_CCS_2:
    case ConnectorType.C_G105:
      connector.value = Object.assign(connector.value, {
        maxAmperage: 125,
        phases: Phase.UNKNOWN_NA,
        voltage: 400,
        current: Current.DC
      });
      currentLocked.value = true;
      phaseLocked.value = true;
      break;
    case ConnectorType.CEE_7_7:
      connector.value = Object.assign(connector.value, {
        current: Current.AC
      });
      currentLocked.value = true;
      break;
    case ConnectorType.S_BS1361:
      connector.value = Object.assign(connector.value, {
        current: Current.AC
      });
      currentLocked.value = true;
      break;
    case ConnectorType.S_309_1P_16A:
      connector.value = Object.assign(connector.value, {
        current: Current.AC,
        maxAmperage: 16,
        phases: Phase.ONE
      });
      currentLocked.value = true;
      phaseLocked.value = true;
      break;
    case ConnectorType.S_309_3P_16A:
      connector.value = Object.assign(connector.value, {
        current: Current.AC,
        maxAmperage: 16,
        phases: Phase.THREE
      });
      currentLocked.value = true;
      phaseLocked.value = true;
      break;
    case ConnectorType.S_309_3P_32A:
      connector.value = Object.assign(connector.value, {
        current: Current.AC,
        maxAmperage: 32,
        phases: Phase.THREE
      });
      currentLocked.value = true;
      phaseLocked.value = true;
      break;
    case ConnectorType.UNSPECIFIED:
    default:
      connector.value = Object.assign(connector.value, {
        maxAmperage: undefined,
        voltage: undefined,
        phases: Phase.UNKNOWN_NA,
        current: Current.UNSPECIFIED
      });
      break;
  }
}
</script>

<style lang="scss" scoped>
.full-width-grid-row {
  grid-column: span 2 / span 2;
}
</style>

<i18n>
{
  "en": {
    "connector": "Connector {id}",
    "maxAmperage": {
      "placeholder": "Enter max. amperage (A)",
      "label": "Max. amperage (A)"
    },
    "maxElectricPower": {
      "label": "Max. electric power (kW)"
    },
    "phase": {
      "placeholder": "Select amount of phases",
      "label": "Phase(s)",
      "options": {
        "0": "Unknown or n/a",
        "1": "Single phase",
        "2": "Two phase",
        "3": "Three phase"
      }
    },
    "voltage": {
      "placeholder": "Enter voltage (V)",
      "label": "Voltage (V)"
    },
    "connectorType": {
      "placeholder": "Select connector type",
      "label": "Connector type",
      "options": {
        "UNSPECIFIED": "Not specified. Excluded from OCPI.",
        "C_TYPE_1": "Cabled, IEC62196-2 Type 1 (J1772)",
        "C_TYPE_2": "Cabled, IEC62196-2 Type 2 (Mennekes)",
        "C_G105": "Cabled, CHAdeMO, DC",
        "C_CCS_1": "Cabled, Combined Charging System 1 (Combo 1), DC",
        "C_CCS_2": "Cabled, Combined Charging System 2 (Combo 2), DC",
        "CEE_7_7": "Socket, CEE 7/7 (Schuko), 16A",
        "S_309_1P_16A": "Socket, IEC60309, single phase, 16A",
        "S_309_3P_16A": "Socket, IEC60309, three phase, 16A",
        "S_309_3P_32A": "Socket, IEC60309, three phase, 32A",
        "S_TYPE_2": "Socket IEC62196-2 Type 2 (Mennekes)",
        "S_TYPE_3": "Socket IEC62196-2 Type 3 (Scame)",
        "S_BS1361": "Socket, UK domestic"
      },
      "ocpiWarning": "Warning. This unspecified connector type is excluded from OCPI, and may lead to undesired behaviour. If possible, please select a different connector type."
    },
    "current": {
      "placeholder": "Select current",
      "label": "Current",
      "options": {
        "UNSPECIFIED": "Not specified",
        "AC": "AC",
        "DC": "DC"
      }
    },
    "capabilities": {
      "label": "Capabilities"
    }
  },
  "nl": {
    "connector": "Aansluiting {id}",
    "maxAmperage": {
      "placeholder": "Voer max. stroomsterkte (A) in",
      "label": "Max. stroomsterkte (A)"
    },
    "maxElectricPower": {
      "label": "Max. elektrisch vermogen (kW)"
    },
    "phase": {
      "placeholder": "Selecteer aantal fasen",
      "label": "Fase(n)",
      "options": {
        "0": "Onbekend of n.v.t.",
        "1": "Enkele fase",
        "2": "Twee fasen",
        "3": "Drie fasen"
      }
    },
    "voltage": {
      "placeholder": "Voer spanning (V) in",
      "label": "Spanning (V)"
    },
    "connectorType": {
      "placeholder": "Selecteer aansluitingstype",
      "label": "Aansluitingstype",
      "options": {
        "UNSPECIFIED": "Niet ingevuld. Uitgesloten van OCPI.",
        "C_TYPE_1": "Vaste kabel, IEC62196-2 Type 1 (J1772)",
        "C_TYPE_2": "Vaste kabel, IEC62196-2 Type 2 (Mennekes)",
        "C_G105": "Vaste kabel, CHAdeMO, DC",
        "C_CCS_1": "Vaste kabel, Combined Charging System 1 (Combo 1), DC",
        "C_CCS_2": "Vaste kabel, Combined Charging System 2 (Combo 2), DC",
        "CEE_7_7": "Socket, CEE 7/7 (Schuko), 16A",
        "S_309_1P_16A": "Socket, IEC60309, enkele fase, 16A",
        "S_309_3P_16A": "Socket, IEC60309, drie fasen, 16A",
        "S_309_3P_32A": "Socket, IEC60309, drie fasen, 32A",
        "S_TYPE_2": "Socket, IEC62196-2 Type 2 (Mennekes)",
        "S_TYPE_3": "Socket, IEC62196-2 Type 3 (Scame)",
        "S_BS1361": "Socket, UK domestic"
      },
      "ocpiWarning": "Waarschuwing. Dit aansluitingstype wordt uitgesloten in OCPI. Dit kan leiden tot ongewenst gedrag. Selecteer indien mogelijk een ander aansluitingstype."
    },
    "current": {
      "placeholder": "Selecteer stroomsoort",
      "label": "Stroomsoort",
      "options": {
        "0": "Niet gespecificeerd",
        "1": "AC",
        "2": "DC"
      }
    },
    "capabilities": {
      "label": "Functies"
    }
  }
}
</i18n>
